import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpenCheck, ShieldCheck, BarChart3, Wallet, Users, ScanLine, MessageSquare, IdCard, MapPin, Phone, Globe } from "lucide-react";

export const LOGO_URL = "/dfa-logo.svg";

const Mark = ({ light = false }) => (
  <div className="inline-flex items-center gap-3 group" data-testid="brand-mark">
    <img src={LOGO_URL} alt="DFA Logo" className="w-11 h-11 rounded-full bg-white p-0.5 ring-1 ring-[#9DCD3F]/50 shadow-sm" />
    <div className="flex flex-col leading-tight">
      <span className={"font-display text-sm tracking-wide " + (light ? "text-white" : "text-[#1B2C5A]")}>DARUSSALAM</span>
      <span className="overline text-[#9DCD3F]">Foundation Academy</span>
    </div>
  </div>
);

const features = [
  { icon: Users,         title: "Student & Staff Management", body: "Comprehensive admissions, profiles, classes and HR records — one secure source of truth." },
  { icon: ScanLine,      title: "QR + Photo Attendance",      body: "Instant in/out logs with QR scanning, webcam photo capture and manual override." },
  { icon: Wallet,        title: "Fees & POS Payments",        body: "Issue invoices, accept cash, card, bank or mobile money, and print receipts." },
  { icon: BookOpenCheck, title: "Exams & Report Cards",       body: "Record marks subject by subject and generate beautiful printable report cards." },
  { icon: BarChart3,     title: "Real-time Analytics",         body: "Director dashboards with attendance trends, gender, classes and fee collections." },
  { icon: ShieldCheck,   title: "Roles & Audit Trail",         body: "Nine secure roles. Every change captured with who, what and when." },
  { icon: IdCard,        title: "ID Cards with QR",            body: "One-click ID card generation with embedded QR code for instant verification." },
  { icon: MessageSquare, title: "Parent Portal & Complaints",  body: "Parents see grades, fees and invoices. Submit and track complaints to resolution." },
];

const Landing = () => {
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#FDFBF7] text-[#0F172A]" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-[#E2E8F0]/70">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-2 px-3 sm:px-6 lg:px-10">
          <Mark />
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#475569] font-medium">
            <a href="#about" className="hover:text-[#1B2C5A] transition" data-testid="nav-about">About</a>
            <a href="#features" className="hover:text-[#1B2C5A] transition" data-testid="nav-features">Platform</a>
            <a href="#admissions" className="hover:text-[#1B2C5A] transition" data-testid="nav-admissions">Admissions</a>
            <a href="#programs" className="hover:text-[#1B2C5A] transition" data-testid="nav-programs">Programs</a>
            <a href="#contact" className="hover:text-[#1B2C5A] transition" data-testid="nav-contact">Contact</a>
          </nav>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="header-login-link" className="hidden sm:block">
              <Button variant="ghost" className="text-[#1B2C5A] hover:bg-[#1B2C5A]/5">Sign in</Button>
            </Link>
            <Link to="/login" data-testid="header-cta">
              <Button className="rounded-full bg-[#1B2C5A] px-3 text-white hover:bg-[#14224A] sm:px-5">
                <span className="hidden sm:inline">Open </span>Portal <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-[#9DCD3F]/15 text-[#1B2C5A] border border-[#9DCD3F]/30">
              <span className="w-2 h-2 rounded-full bg-[#9DCD3F]" />
              <span className="text-xs font-medium tracking-wide uppercase">Admission 2026/2027 — Now Open</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1B2C5A] leading-[0.95] tracking-tight">
              Knowledge.<br />
              <span className="italic text-[#2A3F7A]">Character.</span><br />
              <span className="text-[#9DCD3F]">Excellence.</span>
            </h1>
            <p className="mt-8 text-lg max-w-xl text-[#475569] leading-relaxed">
              Darussalam Foundation Academy nurtures a generation that carries Islamic values and academic mastery —
              from Crèche through Senior Secondary, plus Islamiyyah & Tahfīz programs.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/login" data-testid="hero-cta-primary">
                <Button size="lg" className="bg-[#1B2C5A] hover:bg-[#14224A] text-white rounded-full px-7 h-12">
                  Enter Portal <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <a href="#programs" data-testid="hero-cta-secondary">
                <Button size="lg" variant="outline" className="rounded-full px-7 h-12 border-[#1B2C5A]/20 text-[#1B2C5A] hover:bg-[#1B2C5A]/5">
                  Our Programs
                </Button>
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 max-w-md gap-6">
              {[
                { k: "9", v: "Role-based portals" },
                { k: "K-SS3", v: "All grades covered" },
                { k: "24/7", v: "Parent visibility" },
              ].map((s) => (
                <div key={s.k} className="border-l-2 border-[#9DCD3F] pl-4">
                  <div className="font-serif text-3xl text-[#1B2C5A]">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest text-[#475569] mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-[#E2E8F0]">
              <img
                src="https://images.pexels.com/photos/14921312/pexels-photo-14921312.jpeg"
                alt="Darussalam Foundation Academy building"
                className="w-full h-[440px] lg:h-[560px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B2C5A]/75 via-[#1B2C5A]/10 to-transparent" />
              <div className="absolute top-6 right-6">
                <img src={LOGO_URL} alt="DFA" className="w-16 h-16 rounded-full bg-white/95 p-1 ring-2 ring-[#9DCD3F]/40 shadow-lg" />
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="overline text-[#9DCD3F]">Academy Motto</div>
                <p className="font-serif text-3xl leading-snug mt-1 italic">
                  "Knowledge & Action."
                </p>
              </div>
            </div>
            <div className="hidden lg:block absolute -bottom-10 -left-10 w-48 bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-md">
              <div className="overline text-[#9DCD3F]">Today</div>
              <div className="font-display text-3xl text-[#1B2C5A] mt-1">96.4%</div>
              <div className="text-xs text-[#475569] mt-1">Attendance across all classes</div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline text-[#9DCD3F]">About the Academy</div>
            <h2 className="font-serif text-4xl text-[#1B2C5A] mt-3 leading-tight">
              Faith. Excellence.<br /> Modern Operations.
            </h2>
            <p className="text-[#475569] mt-5 text-sm font-serif italic">أكاديمية دار السلام الإسلامية</p>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[#475569] leading-relaxed text-[17px]">
            <p>
              Darussalam Foundation Academy is committed to nurturing students who carry both
              <span className="text-[#1B2C5A] font-medium"> Islamic values</span> and the academic mastery the modern world demands.
            </p>
            <p>
              From Crèche & Kindergarten through Senior Secondary, plus dedicated Islamiyyah & Tahfīzul Qur'an sections,
              every administrator, teacher, parent and student now operates from a single secure platform —
              purpose-built for an Islamic educational institution that takes
              <span className="text-[#1B2C5A] font-medium"> trust and transparency</span> seriously.
            </p>
            <div className="grid grid-cols-2 gap-4 pt-4">
              {[
                "Qualified & Experienced Teachers",
                "Islamic & Western Education",
                "Safe & Conducive Environment",
                "ICT-Based Learning",
                "Moral Discipline",
                "Affordable Quality Education",
              ].map((p) => (
                <div key={p} className="flex items-center gap-2 text-sm text-[#1B2C5A]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#9DCD3F]" /> {p}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section id="programs" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="overline text-[#9DCD3F]">Programs</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#1B2C5A] mt-3 leading-tight">
            From Crèche to Senior Secondary — and the Qur'an.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { title: "Early Years", body: "Crèche · Kindergarten · Nursery", color: "#9DCD3F" },
            { title: "Primary (Basic)", body: "Primary 1 — Primary 6", color: "#1B2C5A" },
            { title: "Secondary", body: "JSS 1–3 · SS 1–3", color: "#C9A24A" },
            { title: "Islamiyyah", body: "Nurul Bayan · Tajweed · Aqeedah · Fiqh · Hadith · Arabic", color: "#1B2C5A" },
            { title: "Tahfīzul Qur'an", body: "Full memorization · Daily Murājaʿah · Tajweed perfection", color: "#9DCD3F" },
            { title: "Co-curricular", body: "ICT · Sports · Moral & Character Development", color: "#C9A24A" },
          ].map((p, i) => (
            <div key={p.title} className="rounded-2xl border border-[#E2E8F0] bg-white p-6 hover:-translate-y-1 hover:shadow-md transition-all" data-testid={`program-${i}`}>
              <div className="w-2 h-10 rounded-full mb-4" style={{ background: p.color }} />
              <h3 className="font-display text-xl text-[#1B2C5A]">{p.title}</h3>
              <p className="text-sm text-[#475569] mt-2 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features grid (Tetris) */}
      <section id="features" className="bg-white border-y border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="overline text-[#9DCD3F]">Platform</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#1B2C5A] mt-3 leading-tight max-w-2xl">
              One system. Every department. Every role.
            </h2>
          </div>
          <p className="max-w-md text-[#475569]">
            Nine role-based portals — from Super Admin to Parent — share the same single
            source of truth, with permissions that fit each responsibility.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={
                "group relative rounded-2xl border border-[#E2E8F0] bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-md " +
                (i % 5 === 0 ? "lg:col-span-2 lg:row-span-1" : "")
              }
              data-testid={`feature-card-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#1B2C5A]/5 text-[#1B2C5A] grid place-items-center mb-5 group-hover:bg-[#1B2C5A] group-hover:text-[#9DCD3F] transition-colors">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-lg text-[#1B2C5A] leading-tight">{f.title}</h3>
              <p className="text-sm text-[#475569] mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
      </section>

      {/* Admissions CTA */}
      <section id="admissions" className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="relative overflow-hidden rounded-3xl bg-[#1B2C5A] text-white p-10 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 relative z-10">
              <div className="overline text-[#9DCD3F]">Admissions 2026/2027</div>
              <h3 className="font-serif text-4xl lg:text-5xl mt-3 leading-tight">
                Admission Open. Limited spaces available.
              </h3>
              <p className="mt-5 text-white/80 max-w-xl">
                Sign in to your portal as parent, student, teacher or staff. Admins can begin
                a new admission directly from the dashboard.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/login" data-testid="cta-portal">
                  <Button size="lg" className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A] rounded-full px-7 h-12 font-semibold">
                    Sign in to Portal <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
                <a href="#contact" data-testid="cta-contact">
                  <Button size="lg" variant="outline" className="rounded-full px-7 h-12 border-white/30 text-white bg-transparent hover:bg-white/10 hover:text-white">
                    Contact Admissions
                  </Button>
                </a>
              </div>
            </div>
            <div className="lg:col-span-5 relative z-10">
              <div className="rounded-2xl overflow-hidden border border-[#9DCD3F]/30">
                <img
                  src="https://images.pexels.com/photos/8199143/pexels-photo-8199143.jpeg"
                  alt="Students learning"
                  className="w-full h-72 object-cover"
                />
              </div>
            </div>
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#9DCD3F]/15 blur-3xl" />
            <div className="absolute -left-20 -bottom-32 w-96 h-96 rounded-full bg-[#2A3F7A] blur-3xl opacity-50" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#14224A] text-white/85">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Mark light />
            <p className="mt-5 max-w-sm text-sm text-white/65">
              Cloud-based School Management System for Darussalam Foundation Academy —
              powered by a secure, role-based platform.
            </p>
            <p className="mt-3 text-[#9DCD3F] text-sm italic font-serif">"Knowledge & Action"</p>
          </div>
          <div className="lg:col-span-3">
            <div className="overline text-[#9DCD3F] mb-3">Contact</div>
            <p className="text-sm flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#9DCD3F]" /> +234 703 616 1413</p>
            <p className="text-sm flex items-center gap-2 mt-1"><Phone className="w-3.5 h-3.5 text-[#9DCD3F]" /> +234 700 315 8938</p>
            <p className="text-sm flex items-center gap-2 mt-1"><Globe className="w-3.5 h-3.5 text-[#9DCD3F]" /> www.dfa.com.ng</p>
          </div>
          <div className="lg:col-span-4">
            <div className="overline text-[#9DCD3F] mb-3">Address</div>
            <p className="text-sm flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#9DCD3F] mt-0.5 shrink-0" />
              <span>Shehu Idris Road, Emanto by Kabama Layout,<br />Before Maraba Clinic, off Sokoto Road,<br />Zaria, Kaduna State, Nigeria.</span>
            </p>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-xs text-white/50">
          © {new Date().getFullYear()} Darussalam Foundation Academy. Building a Better Tomorrow, One Child at a Time.
        </div>
      </footer>
    </div>
  );
};

export default Landing;
